import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useCountdown } from "@/hooks/useCountdown";
import { useAbVariant } from "@/hooks/useAbVariant";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ease = [0.16, 1, 0.3, 1];

const PACKAGES = [
  "Photography, from £3,000",
  "Photography & Film, from £4,000",
];

const STEPS = ["The Two of You", "The Day", "The Vision"];

export default function ApplicationForm() {
  const navigate = useNavigate();
  const { formatted } = useCountdown();
  const { copy, variant } = useAbVariant();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    full_name: "",
    partner_name: "",
    email: "",
    phone: "",
    wedding_date: "",
    venue: "",
    location: "",
    guest_count: "",
    package_tier: "",
    vision: "",
    heard_from: "",
  });

  const set = (k) => (e) => {
    setForm({ ...form, [k]: e.target.value });
    if (errors[k]) setErrors({ ...errors, [k]: null });
  };

  const validateStep = () => {
    const e = {};
    if (step === 0) {
      if (!form.full_name.trim()) e.full_name = "Required";
      if (!form.email.trim()) e.email = "Required";
      else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Invalid email";
      if (!form.phone.trim()) e.phone = "Required";
    }
    if (step === 2) {
      if (!form.package_tier) e.package_tier = "Please choose a starting point";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = (ev) => {
    ev?.preventDefault();
    if (!validateStep()) return;
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/applications`, {
        ...form,
        cta_variant: variant,
      });
      toast.success("Application received. We'll be in touch within 24 hours.");
      navigate("/confirmation", { state: { application: res.data } });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email hello@chicwedding.co.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <section
      id="apply"
      data-testid="application-section"
      className="px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-[#F6F1E8]"
    >
      <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10 lg:gap-16 items-start">
        <div className="lg:col-span-4">
          <p className="eyebrow text-muted-foreground mb-6 hidden">Apply</p>
          <h2 className="font-serif-display tracking-tight leading-[0.95] text-3xl sm:text-4xl md:text-5xl">
            Reserve your
            <br />
            <span className="italic">private consultation.</span>
          </h2>
          <div className="hairline my-10 max-w-[260px]" />
          <p className="text-foreground/75 leading-relaxed max-w-md">
            A short application. We reply personally within 24 hours.
          </p>
          <div className="mt-10 p-6 border border-border bg-secondary">
            <p className="eyebrow text-muted-foreground mb-3">Offer Closes</p>
            <p
              data-testid="apply-countdown"
              className="font-serif-display italic text-3xl tracking-tight tabular-nums"
            >
              {formatted}
            </p>
            <p className="text-sm text-foreground/70 mt-2">
              A £500 gift, currently complimentary.
            </p>
          </div>
        </div>

        <div className="lg:col-span-8">
          {/* Step indicator */}
          <div className="flex items-center gap-4 mb-12 flex-wrap">
            {STEPS.map((label, i) => (
              <div
                key={label}
                data-testid={`step-indicator-${i}`}
                className="flex items-center gap-3"
              >
                <span
                  className={`font-serif-display italic text-xl ${
                    i === step
                      ? "text-foreground"
                      : i < step
                      ? "text-foreground/40 line-through"
                      : "text-foreground/30"
                  }`}
                >
                  0{i + 1}
                </span>
                <span
                  className={`eyebrow ${
                    i === step ? "text-foreground" : "text-foreground/40"
                  }`}
                >
                  {label}
                </span>
                {i < STEPS.length - 1 && (
                  <span className="w-10 h-px bg-border" />
                )}
              </div>
            ))}
          </div>

          <form onSubmit={step === STEPS.length - 1 ? submit : next} noValidate>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease }}
                className="grid md:grid-cols-2 gap-x-10 gap-y-8"
              >
                {step === 0 && (
                  <>
                    <Field
                      label="Your full name"
                      testid="field-full-name"
                      value={form.full_name}
                      onChange={set("full_name")}
                      error={errors.full_name}
                      required
                    />
                    <Field
                      label="Partner's name (optional)"
                      testid="field-partner-name"
                      value={form.partner_name}
                      onChange={set("partner_name")}
                    />
                    <Field
                      label="Email"
                      type="email"
                      testid="field-email"
                      value={form.email}
                      onChange={set("email")}
                      error={errors.email}
                      required
                    />
                    <Field
                      label="Telephone"
                      testid="field-phone"
                      value={form.phone}
                      onChange={set("phone")}
                      error={errors.phone}
                      required
                    />
                  </>
                )}

                {step === 1 && (
                  <>
                    <Field
                      label="Wedding date (or month)"
                      testid="field-wedding-date"
                      value={form.wedding_date}
                      onChange={set("wedding_date")}
                      placeholder="e.g. 14 June 2026"
                    />
                    <Field
                      label="Estimated guest count"
                      testid="field-guest-count"
                      value={form.guest_count}
                      onChange={set("guest_count")}
                      placeholder="e.g. 120"
                    />
                    <Field
                      label="Venue (if known)"
                      testid="field-venue"
                      value={form.venue}
                      onChange={set("venue")}
                      placeholder="e.g. Hedsor House"
                    />
                    <Field
                      label="Location / region"
                      testid="field-location"
                      value={form.location}
                      onChange={set("location")}
                      placeholder="Essex, London, Kent, Suffolk…"
                    />
                  </>
                )}

                {step === 2 && (
                  <>
                    <div className="md:col-span-2">
                      <label className="eyebrow text-muted-foreground block mb-4">
                        Where would you like to begin? *
                      </label>
                      <div className="grid md:grid-cols-2 gap-3">
                        {PACKAGES.map((p) => {
                          const active = form.package_tier === p;
                          return (
                            <button
                              type="button"
                              key={p}
                              data-testid={`pkg-${p
                                .toLowerCase()
                                .replace(/&/g, "and")
                                .replace(/[^a-z0-9]+/g, "-")
                                .replace(/^-|-$/g, "")}`}
                              onClick={() =>
                                setForm({ ...form, package_tier: p })
                              }
                              className={`text-left p-5 border transition-all ${
                                active
                                  ? "bg-[#1A1A1A] text-[#F6F1E8] border-[#1A1A1A]"
                                  : "bg-transparent text-foreground border-border hover:border-foreground"
                              }`}
                            >
                              <p className="eyebrow opacity-70">Option</p>
                              <p className="font-serif-display text-xl tracking-tight mt-2">
                                {p}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                      {errors.package_tier && (
                        <p className="mt-3 text-xs text-destructive eyebrow">
                          {errors.package_tier}
                        </p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="eyebrow text-muted-foreground block mb-3">
                        Tell us about your vision
                      </label>
                      <textarea
                        data-testid="field-vision"
                        value={form.vision}
                        onChange={set("vision")}
                        rows={5}
                        placeholder="Style, mood, must-have moments, anything that matters…"
                        className="w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 font-sans-ui text-base leading-relaxed transition-colors"
                      />
                    </div>

                    <Field
                      label="How did you hear about us?"
                      testid="field-heard-from"
                      value={form.heard_from}
                      onChange={set("heard_from")}
                      placeholder="Instagram, a friend, your venue…"
                    />
                  </>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-16 flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={back}
                disabled={step === 0}
                data-testid="form-back"
                className={`eyebrow ${
                  step === 0
                    ? "opacity-30 cursor-not-allowed"
                    : "link-underline"
                }`}
              >
                ← Back
              </button>

              {step < STEPS.length - 1 ? (
                <button
                  type="submit"
                  data-testid="form-next"
                  className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F6F1E8] px-8 py-5 eyebrow hover:bg-black transition-colors"
                >
                  Continue →
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="form-submit"
                  className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F6F1E8] px-8 py-5 eyebrow hover:bg-black disabled:opacity-50 transition-colors"
                >
                  {submitting ? "Sending…" : "Submit My Application →"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testid, error, required, type = "text", ...rest }) {
  return (
    <div>
      <label className="eyebrow text-muted-foreground block mb-3">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        data-testid={testid}
        {...rest}
        className="w-full bg-transparent border-b border-border focus:border-foreground outline-none py-3 font-sans-ui text-base transition-colors"
      />
      {error && (
        <p className="mt-2 text-xs text-destructive eyebrow">{error}</p>
      )}
    </div>
  );
}
